import { Component, Injector, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Subscription } from 'rxjs';
import { User, UserTab } from '../../../auth/models';
import { DateTimeHelper } from '../../../common/helpers/datetime/datetime.helper';
import { CurrencyCollection, FlightType, FuelPreferenceCollection, UnitsCollection } from "../../../common/models";
import { FuelType } from "../../../common/models/fuel-type";
import { FlightTypeService } from '../../../common/services/flight-type/flight-type.service';
import { ToastService } from '../../../main/services';
import { BidFuelTypeService } from '../../../supplier/services/bid-fuel-type/bid-fuel-type.service';
import { Contract, Round, Tender, TenderStats } from '../../models';
import { LocationTagsService, TenderBidsService, TenderContractsAwardedService, TenderContractsStatisticService, TenderSuppliersService } from '../../services';
import { ContractFormModalComponent } from "../create-tender-contracts";

@Component({
    selector: 'jft-tender-bids',
    templateUrl: './tender-bids.component.html',
    styleUrls: ['./tender-bids.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TenderBidsComponent implements OnInit, OnDestroy {

    public tender: Tender;
    public stats: TenderStats;
    public orderField: string;
    public order: string;
    public currenciesCollection: CurrencyCollection;
    public unitsCollection: UnitsCollection;
    public fuelPreferenceCollection: FuelPreferenceCollection;

    public modalReference: any;

    // Filter properties
    public companyOptions: any[] = [];
    public fuelTypeOptions: FuelType[] = [];
    public flightTypeOptions: FlightType[] = [];
    public supplierOptions: any[] = [];
    public locationTagOptions: UserTab[] = [];

    public selectedCompanies: any[] = [];
    public selectedFuelTypes: number[] = [];
    public selectedFlightTypes: number[] = [];
    public selectedSuppliers: number[] = [];
    public selectedLocationTags: number[] = [];

    // Round filter
    public roundOptions: {value: number | null, label: string}[] = [];
    public selectedRound: number | null = null;
    public maxRound: number = 1;
    // Mobile-only collapse state for the shell toolbar.
    public actionsOpen: boolean = false;
    public filtersOpen: boolean = false;
    private maxRoundSubscription: Subscription;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        protected toast: ToastService,
        private contractsService: TenderBidsService,
        private contractsAwardedService: TenderContractsAwardedService,
        private statsService: TenderContractsStatisticService,
        private injector: Injector,
        private ngbModal: NgbModal,
        public user: User,
        private tenderSuppliersService: TenderSuppliersService,
        private fuelTypeService: BidFuelTypeService,
        private flightTypeService: FlightTypeService,
        private locationTagsService: LocationTagsService
    ) {
    }

    public ngOnInit() {
        this.dataInit();
        this.initializeFilters();
        this.subscribeToStatsUpdates();
        this.subscribeToMaxRound();
    }

    public search(term) {
        this.contractsService.termObservable.next(term);
        this.contractsAwardedService.searchFieldObservable.next(term);
    }

    private dataInit() {
        let data = this.route.snapshot.data,
            parentData = this.route.parent.snapshot.data;

        this.stats = data['stats'];
        this.tender = parentData['tender'];

        // Force refresh stats to ensure they're up to date
        this.fetchStats();

        this.currenciesCollection = data['currenciesCollection'];
        this.unitsCollection = data['unitsCollection'];
        this.fuelPreferenceCollection = data['fuelPreferenceCollection'];
    }

    public ngOnDestroy(): void {
        if (this.maxRoundSubscription) {
            this.maxRoundSubscription.unsubscribe();
        }
    }

    public isDateExpired(date) {
        return DateTimeHelper.isDateExpired(date)
    }

    public addContract() {
        let contract = Contract.create({
            date_start: this.tender.dateContractStart.toISOString(),
            date_end: this.tender.dateContractEnd.toISOString(),
            units: this.unitsCollection.getById(this.tender.unitsId).toJSON(),
            currency: this.currenciesCollection.getById(this.tender.currencyId).toJSON(),
            fuel_preference: this.fuelPreferenceCollection.getById(1) //into plane
        });

        this._addContract(contract);
    }

    private _addContract(contractData: Contract) {
        this.modalReference = this.ngbModal.open(ContractFormModalComponent, {
            injector: Injector.create([
                {provide: Contract, useValue: contractData},
                {provide: Tender, useValue: this.tender},
                {provide: CurrencyCollection, useValue: this.currenciesCollection},
                {provide: UnitsCollection, useValue: this.unitsCollection},
                {provide: FuelPreferenceCollection, useValue: this.fuelPreferenceCollection}
            ], this.injector),
            size: 'lg',
            backdrop: 'static'
        }).result.then((contract: Contract) => {
            this.addContractToLaunchedTender(contract);
            this.fetchStats();
        }, (error) => {
            console.error(error)
        });
    }

    private addContractToLaunchedTender(contract: Contract) {
        this.contractsService.addContractToLaunchedTender(this.tender, contract).subscribe(data => {
            this.contractsService.updateContractsObservable.next(contract);
        }, (e) => {
            this.toast.error(e);
        });
    }

    private fetchStats(){
        this.statsService.fetch(this.tender.id).subscribe(data => {
            this.stats = data;
        }, (e) => {
            this.toast.error(e);
        });

    }

    public refreshStats(){
        this.statsService.refresh(this.tender.id).subscribe(data => {
            this.stats = data;
            this.toast.success('Statistics updated successfully');
        }, (e) => {
            this.toast.error(e);
        });
    }

    public forceRefreshStats(){
        // Force refresh without showing success message - used internally
        this.statsService.refresh(this.tender.id).subscribe(data => {
            this.stats = data;
        }, (e) => {
            // Error handled silently for internal refresh
        });
    }

    public onRoundExtend(round: Round) {
        this.contractsService.updateContractsObservable.next(round);
    }

    public onRoundLaunchedAllLocations(round: Round) {
        this.contractsService.updateContractsObservable.next(round);
    }

    private subscribeToStatsUpdates() {
        // Listen for stats updates from child components (like tender-contracts-all)
        this.contractsService.updateContractsObservable.subscribe((data: any) => {
            if (data && data.type === 'stats_updated' && data.stats) {
                this.stats = data.stats;
            }
        });
    }

    public canShowAdvancedActions(): boolean {
        // isDone(), not isCancelled(): advanced actions are hidden on every
        // finished tender — expired and completed as well as cancelled — which
        // is how tender.component.html already gates them.
        return !this.tender.isDone() && this.user.canManageAdvanced();
    }

    public showFilters(): boolean {
        return true; // Always show filters for buyer view
    }

    private initializeFilters() {
        this.loadFuelTypes();
        this.loadFlightTypes();
        this.loadSuppliers();
        this.loadCompanyOptions();
        this.loadLocationTags();
    }

    private loadFuelTypes() {
        this.fuelTypeService.fetch().subscribe((data: any) => {
            // Handle the response based on the actual service return type
            if (Array.isArray(data)) {
                this.fuelTypeOptions = data;
            } else {
                this.fuelTypeOptions = [];
            }
        }, (error) => {
            console.error('Error loading fuel types:', error);
            this.fuelTypeOptions = [];
        });
    }

    private loadFlightTypes() {
        this.flightTypeService.fetch().subscribe((flightTypes: FlightType[]) => {
            this.flightTypeOptions = flightTypes;
        }, (error) => {
            console.error('Error loading flight types:', error);
            this.flightTypeOptions = [];
        });
    }

    private loadSuppliers() {
        const handleSuppliers = (suppliers: any[]) => {
            this.supplierOptions = suppliers.map((s: any) => ({
                id: s.id,
                name: s.name || `Supplier ${s.id}`
            })).sort((a: any, b: any) => a.name.localeCompare(b.name));
        };
        const handleError = (error) => {
            console.error('Error loading suppliers:', error);
            this.supplierOptions = [];
        };

        if (this.selectedRound) {
            // When a specific round is selected, load suppliers scoped to that round
            this.tenderSuppliersService.fetchLastRound(this.tender.id, [], 'all', 1, this.selectedRound, 100).subscribe((result: any) => {
                const suppliers = result.results || result;
                if (Array.isArray(suppliers)) {
                    handleSuppliers(suppliers);
                } else {
                    this.supplierOptions = [];
                }
            }, handleError);
        } else {
            // Default: load all invited suppliers
            this.tenderSuppliersService.fetch(this.tender.id).subscribe((suppliers: any) => {
                if (Array.isArray(suppliers)) {
                    handleSuppliers(suppliers);
                } else {
                    this.supplierOptions = [];
                }
            }, handleError);
        }
    }

    private loadCompanyOptions() {
        // Always load company_info values from contracts
        // This handles both cases: companies with and without child airlines
        this.loadCompanyInfoOptions();
    }

    private loadCompanyInfoOptions() {
        // Fetch contracts to get unique company_info values
        this.contractsService.fetch(this.tender.id).subscribe((result: any) => {
            let contracts = [];
            if (Array.isArray(result)) {
                contracts = result;
            } else if (result.contracts) {
                contracts = result.contracts;
            }

            // Extract unique company names (using alias if available)
            const uniqueCompanies = new Set();
            contracts.forEach(contract => {
                // Prefer companyChild with alias, then companyChild name, then companyInfo
                if (contract.companyChild && contract.companyChild.name) {
                    const displayName = contract.companyChild.getDisplayName();
                    uniqueCompanies.add(displayName);
                } else if (contract.companyInfo && contract.companyInfo.trim()) {
                    uniqueCompanies.add(contract.companyInfo.trim());
                }
            });

            // Convert to options format
            this.companyOptions = Array.from(uniqueCompanies).map((company: string) => ({
                name: company,
                value: company
            })).sort((a, b) => a.name.localeCompare(b.name));
        }, (error) => {
            console.error('Error loading company options:', error);
            this.companyOptions = [];
        });
    }

    public onCompanyFilterChange() {
        this.applyFilters();
    }

    public onFuelTypeFilterChange() {
        this.applyFilters();
    }

    public onFlightTypeFilterChange() {
        this.applyFilters();
    }

    public onSupplierFilterChange() {
        this.applyFilters();
    }

    public onLocationTagFilterChange() {
        // Save selected tags to localStorage for this user
        this.locationTagsService.saveSelectedTags(this.selectedLocationTags);
        this.applyFilters();
    }

    public onRoundFilterChange() {
        // Reload suppliers scoped to the selected round
        this.selectedSuppliers = [];
        this.loadSuppliers();
        this.applyFilters();
    }

    private loadLocationTags() {
        this.locationTagsService.fetchLocationTags().subscribe((tags: UserTab[]) => {
            this.locationTagOptions = tags;
            // Load previously selected tags from localStorage and validate them
            const savedTags = this.locationTagsService.loadSelectedTags();
            this.selectedLocationTags = this.locationTagsService.validateSelectedTags(savedTags, tags);
            // Always apply filters after tags are loaded (triggers child component to fetch data)
            this.applyFilters();
        }, (error) => {
            console.error('Error loading location tags:', error);
            this.locationTagOptions = [];
            this.selectedLocationTags = [];
            // Even on error, apply filters to trigger data fetch
            this.applyFilters();
        });
    }

    private subscribeToMaxRound() {
        this.maxRoundSubscription = this.contractsService.maxRoundSubject.subscribe((maxRound: number) => {
            if (maxRound !== this.maxRound) {
                this.maxRound = maxRound;
                this.buildRoundOptions(maxRound);
            }
        });
    }

    private buildRoundOptions(maxRound: number) {
        this.roundOptions = [
            { value: null, label: 'Current' }
        ];
        for (let i = 1; i <= maxRound; i++) {
            this.roundOptions.push({ value: i, label: `Round ${i}` });
        }
    }

    private applyFilters() {
        // Set filters on the service
        this.contractsService.setCompanyFilter(this.selectedCompanies);
        this.contractsService.setFuelTypeFilter(this.selectedFuelTypes);
        this.contractsService.setFlightTypeFilter(this.selectedFlightTypes);
        this.contractsService.setSupplierFilter(this.selectedSuppliers);
        this.contractsService.setLocationTagFilter(this.selectedLocationTags);
        this.contractsService.setRoundFilter(this.selectedRound);

        // Also set location tag filter on awarded service
        this.contractsAwardedService.setLocationTagFilter(this.selectedLocationTags);

        // Trigger refresh of contracts
        this.contractsService.updateContractsObservable.next(null);
        this.contractsAwardedService.updateContractsObservable.next(null);
    }
}
